import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Flex } from '@repo/ui/Flex';
import { Text } from '../../Language/Language';
import s from './RefundTask.module.css';
import { RadioButton } from '../../RadioButton/RadioButton';
import { useRefundOptions } from '../../../hooks/useRefundOptions';
import { type TaskComponentType } from '../../../workflow/schemaTypes';
import type { RefundResult } from '../../../graphql/query/getRefundOptions';
import { SkeletonLoader } from '../../SkeletonLoader/SkeletonLoader';
import { EmptyMessage } from '../../EmptyMessage/EmptyMessage';
import { clsx } from 'clsx';
import { useIsMobile } from '@repo/lib/hooks/useMediaQuery';

const Loader = () => (
  <SkeletonLoader
    padding="p-16"
    itemHeight={30}
    layout={{
      repeat: 2,
      items: {
        default: [
          {
            columns: 1,
            rows: 1,
          },
          {
            columns: 6,
            rows: 1,
            startColumn: 2,
            startRow: 1,
          },
        ],
        mobile: [
          {
            columns: 2,
            rows: 1,
          },
          {
            columns: 6,
            rows: 1,
            startColumn: 3,
            startRow: 1,
          },
        ],
      },
    }}
  />
);

const Content = ({ type }: { type?: string }) => {
  if (!type) return null;
  return (
    <Flex className={s.lineWrapper} gap="g-12">
      <Flex className={s.title}>
        <Text langKey={`pdx.refund.type.${type.toLowerCase()}`} />
      </Flex>
      <Flex className={s.description}>
        <Text langKey={`pdx.refund.type.${type.toLowerCase()}.description`} />
      </Flex>
    </Flex>
  );
};

export const RefundTask: TaskComponentType = () => {
  const { register, setValue, trigger } = useFormContext();
  const { isLoading, refundOptions, selectedRefundOption } = useRefundOptions();
  const [activeRefundOptions, setActiveRefundOptions] = useState<
    RefundResult['refundOptions']
  >([]);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isLoading) return;
    if (!refundOptions || refundOptions.length === 0) return;

    if (selectedRefundOption) {
      setActiveRefundOptions(refundOptions);
      return;
    }

    if (refundOptions[0]?.type) {
      setValue('refund.method', refundOptions[0].type);
    }

    setActiveRefundOptions(refundOptions);
  }, [isLoading, refundOptions, selectedRefundOption, setValue]);

  const isReady = activeRefundOptions && activeRefundOptions?.length > 0;

  return (
    <Flex
      className={clsx({ [s.refundTask]: true, [s.mobile]: isMobile })}
      direction="column"
      gap="g-12"
    >
      <h2 id="refundTitle" className={s.header}>
        <Text langKey={'pdx.refund.header'} />
      </h2>
      {isLoading && activeRefundOptions.length === 0 && <Loader />}
      {isReady && (
        <div className={s.group}>
          <Flex
            role="radiogroup"
            aria-labelledby="refundTitle"
            direction="column"
          >
            {activeRefundOptions.length > 1 ? (
              activeRefundOptions.map(({ type }) => (
                <RadioButton
                  id={type}
                  key={type}
                  value={type}
                  className={s.radio}
                  disabled={isLoading}
                  {...register(`refund.method`, {
                    onChange: () => {
                      // Trigger form revalidation to ensure watch() picks up changes in Firefox
                      trigger('refund.method');
                    },
                  })}
                >
                  <Content type={type} />
                </RadioButton>
              ))
            ) : (
              <div className={s.radio}>
                <Content type={activeRefundOptions?.[0]?.type} />
              </div>
            )}
          </Flex>
        </div>
      )}
      {!isLoading && activeRefundOptions.length === 0 && (
        <EmptyMessage language={{ langKey: 'pdx.refund.no_methods' }} />
      )}
    </Flex>
  );
};
